import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useUpdateTourStateMutation } from '../../api/tourApi';
import { TourStateData } from '@nicsmith/shared_types';

const KEYS = {
  TOUR_PENDING: 'dmyTourPending',
  TOUR_COMPLETED: 'dmyTourCompleted',
  SEEN_TOOLTIPS: 'dmySeenTooltips',
  CHECKLIST_DISMISSED: 'dmyChecklistDismissed',
} as const;

function readBool(key: string): boolean {
  return localStorage.getItem(key) === 'true';
}

function readLocalTooltips(): string[] {
  try {
    const raw = localStorage.getItem(KEYS.SEEN_TOOLTIPS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Merge DB truth with localStorage fallback */
function initTourCompleted(dbState: TourStateData | undefined): boolean {
  return dbState?.tour_completed ?? readBool(KEYS.TOUR_COMPLETED);
}

function initSeenTooltips(dbState: TourStateData | undefined): Set<string> {
  const dbTooltips = dbState?.seen_tooltips ?? [];
  const localTooltips = readLocalTooltips();
  return new Set([...dbTooltips, ...localTooltips]);
}

function initChecklistDismissed(dbState: TourStateData | undefined): boolean {
  return dbState?.checklist_dismissed ?? readBool(KEYS.CHECKLIST_DISMISSED);
}

export interface TourState {
  isTourPending: boolean;
  isTourCompleted: boolean;
  isTourRunning: boolean;
  markTourPending: () => void;
  startTour: () => void;
  completeTour: () => void;
  skipTour: () => void;
  hasSeenTooltip: (pageId: string) => boolean;
  markTooltipSeen: (pageId: string) => void;
  isChecklistDismissed: boolean;
  dismissChecklist: () => void;
}

export function useTourState(): TourState {
  const dbTourState = useSelector((state: RootState) => state.session.user?.tour_state);
  const [updateTourState] = useUpdateTourStateMutation();

  const [isTourPending, setIsTourPending] = useState(() => readBool(KEYS.TOUR_PENDING));
  const [isTourCompleted, setIsTourCompleted] = useState(() => initTourCompleted(dbTourState));
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [seenTooltips, setSeenTooltips] = useState(() => initSeenTooltips(dbTourState));
  const [isChecklistDismissed, setIsChecklistDismissed] = useState(() => initChecklistDismissed(dbTourState));

  // Keep a ref to latest state for building the full payload
  const stateRef = useRef({ isTourCompleted, seenTooltips, isChecklistDismissed });
  stateRef.current = { isTourCompleted, seenTooltips, isChecklistDismissed };

  const persist = useCallback((overrides: Partial<TourStateData>) => {
    const current = stateRef.current;
    const payload: TourStateData = {
      tour_completed: overrides.tour_completed ?? current.isTourCompleted,
      seen_tooltips: overrides.seen_tooltips ?? [...current.seenTooltips],
      checklist_dismissed: overrides.checklist_dismissed ?? current.isChecklistDismissed,
    };
    // Fire-and-forget — no await
    updateTourState(payload);
  }, [updateTourState]);

  const markTourPending = useCallback(() => {
    localStorage.setItem(KEYS.TOUR_PENDING, 'true');
    setIsTourPending(true);
  }, []);

  const startTour = useCallback(() => {
    setIsTourRunning(true);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.removeItem(KEYS.TOUR_PENDING);
    localStorage.setItem(KEYS.TOUR_COMPLETED, 'true');
    setIsTourPending(false);
    setIsTourCompleted(true);
    setIsTourRunning(false);
    persist({ tour_completed: true });
  }, [persist]);

  const skipTour = useCallback(() => {
    localStorage.removeItem(KEYS.TOUR_PENDING);
    localStorage.setItem(KEYS.TOUR_COMPLETED, 'true');
    setIsTourPending(false);
    setIsTourCompleted(true);
    setIsTourRunning(false);
    persist({ tour_completed: true });
  }, [persist]);

  const hasSeenTooltip = useCallback((pageId: string) => {
    return seenTooltips.has(pageId);
  }, [seenTooltips]);

  const markTooltipSeen = useCallback((pageId: string) => {
    setSeenTooltips(prev => {
      const next = new Set(prev);
      next.add(pageId);
      localStorage.setItem(KEYS.SEEN_TOOLTIPS, JSON.stringify([...next]));
      persist({ seen_tooltips: [...next] });
      return next;
    });
  }, [persist]);

  const dismissChecklist = useCallback(() => {
    localStorage.setItem(KEYS.CHECKLIST_DISMISSED, 'true');
    setIsChecklistDismissed(true);
    persist({ checklist_dismissed: true });
  }, [persist]);

  return {
    isTourPending,
    isTourCompleted,
    isTourRunning,
    markTourPending,
    startTour,
    completeTour,
    skipTour,
    hasSeenTooltip,
    markTooltipSeen,
    isChecklistDismissed,
    dismissChecklist,
  };
}
